<template>
  <section
    class="w-full max-w-[700px] mx-auto px-4 py-2 sm:py-3"
  >
    <div
      class="w-full rounded-2xl border p-5 sm:p-7 space-y-5"
      :class="
        isAfterRamadan
          ? 'border-amber-200 dark:border-amber-900/70 bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.18),_transparent_38%),linear-gradient(to_bottom,_rgba(255,251,235,0.96),_white)] dark:bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.16),_transparent_30%),linear-gradient(to_bottom,_rgba(68,26,3,0.5),_rgba(17,24,39,1))]'
          : 'border-emerald-100 dark:border-emerald-900 bg-gradient-to-b from-emerald-50/70 to-white dark:from-emerald-950/30 dark:to-gray-900'
      "
    >
      <!-- Header with H1 -->
      <header class="text-center">
        <h1 class="sr-only">
          {{ pageHeading }}
        </h1>
        <p class="sr-only">
          {{ snippetParagraph }}
        </p>

        <!-- Immediate Answer Block -->
        <div
          class="rounded-xl p-4 sm:p-6 space-y-4"
          :class="
            isAfterRamadan
              ? 'bg-white/75 dark:bg-amber-950/25 border border-amber-200/80 dark:border-amber-800/60 shadow-[0_18px_50px_-30px_rgba(180,83,9,0.45)]'
              : 'bg-emerald-100 dark:bg-emerald-900/40'
          "
        >
          <template v-if="isAfterRamadan">
            <div class="inline-flex items-center rounded-full border border-amber-300/70 dark:border-amber-700/60 bg-amber-100/80 dark:bg-amber-900/30 px-3 py-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-amber-900 dark:text-amber-100">
              Chand Raat Update
            </div>
            <div class="space-y-2">
              <p class="text-4xl sm:text-6xl leading-none font-black text-amber-950 dark:text-amber-50">
                Rozay end ho chuky hain
              </p>
              <p class="text-lg sm:text-2xl font-semibold text-amber-800 dark:text-amber-200">
                {{ afterRamadanHeadline }}
              </p>
              <p class="text-sm sm:text-base text-amber-900/85 dark:text-amber-100/85">
                {{ todayDateLabel }} · {{ todayHijri }}
              </p>
              <p class="mx-auto max-w-2xl text-sm sm:text-base leading-7 text-amber-900/80 dark:text-amber-100/80">
                {{ heroSupportCopy }}
              </p>
            </div>
            <div class="grid gap-3 sm:grid-cols-3 text-left">
              <div
                v-for="item in afterRamadanHighlights"
                :key="item.label"
                class="rounded-2xl border border-amber-200/80 dark:border-amber-800/60 bg-white/80 dark:bg-gray-950/25 p-4"
              >
                <p class="text-[11px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">
                  {{ item.label }}
                </p>
                <p class="mt-2 text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {{ item.value }}
                </p>
              </div>
            </div>
          </template>

          <template v-else>
            <p
              class="text-7xl sm:text-8xl leading-none font-black text-emerald-900 dark:text-emerald-100 tabular-nums"
            >
              {{ displayRozaDayNumber }}
            </p>
            <div class="space-y-1">
              <p
                class="text-base sm:text-lg font-semibold text-emerald-900 dark:text-emerald-100"
              >
                Aaj ka Roza - Pakistan
              </p>
              <p class="text-xs sm:text-sm font-medium text-emerald-800 dark:text-emerald-200">
                Ramadan Day {{ displayRozaDayNumber }} of {{ TOTAL_RAMADAN_DAYS }}
              </p>
              <p class="text-sm sm:text-base text-emerald-800 dark:text-emerald-200">
                {{ todayDateLabel }}
              </p>
              <p class="text-sm sm:text-base text-emerald-800 dark:text-emerald-200">
                {{ todayHijri }}
              </p>
              <p
                class="pt-1 text-xs sm:text-sm leading-6 text-emerald-800 dark:text-emerald-200"
              >
                {{ heroSupportCopy }}
              </p>
            </div>
            <p
              class="text-base sm:text-lg font-semibold text-emerald-800 dark:text-emerald-200"
            >
              {{ nextEventHeadline }}
            </p>
            <div
              class="mx-auto max-w-xs space-y-2 rounded-xl border border-emerald-200/70 dark:border-emerald-800/60 bg-white/55 dark:bg-gray-950/20 p-3 text-left"
            >
              <p class="text-sm sm:text-base text-emerald-900 dark:text-emerald-100">
                Sehri:
                <span class="font-semibold">{{ sehriTimeDisplay }}</span>
              </p>
              <p class="text-sm sm:text-base text-emerald-900 dark:text-emerald-100">
                Iftar:
                <span class="font-semibold">{{ iftarTimeDisplay }}</span>
              </p>
              <p
                v-if="tomorrowRozaNumber"
                class="text-sm sm:text-base text-emerald-900 dark:text-emerald-100"
              >
                Kal ka Roza:
                <span class="font-semibold">{{ tomorrowRozaNumber }}</span>
              </p>
            </div>
          </template>
        </div>
      </header>

      <!-- Section 1: Live Countdown -->
      <div
        class="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 space-y-3"
      >
        <template v-if="isAfterRamadan">
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-900/20 p-4">
              <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">
                {{ afterRamadanHeadline }}
              </p>
              <p class="mt-2 text-sm leading-6 text-amber-900/80 dark:text-amber-100/80">
                {{ afterRamadanSubtext }}
              </p>
            </div>
            <div class="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
              <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Eid Checklist
              </p>
              <ul class="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Fitrana agar abhi baqi ho to Eid namaz se pehle ada kar dein.</li>
                <li>Kal ki namaz, kapray aur ghar walon ki tayyari aaj raat hi mukammal kar lein.</li>
                <li>Takbeerat aur shukr ke saath chand raat guzarein.</li>
              </ul>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            class="grid grid-cols-4 gap-2"
            role="timer"
            aria-live="polite"
            aria-atomic="true"
          >
            <div
              v-for="item in countdownItems"
              :key="item.label"
              class="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/30 p-2 text-center"
            >
              <div
                class="text-xl sm:text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums"
              >
                {{ item.value }}
              </div>
              <div
                class="mt-0.5 text-[10px] sm:text-xs tracking-wide text-gray-600 dark:text-gray-400"
              >
                {{ item.label }}
              </div>
            </div>
          </div>
          <p
            class="text-center text-xs sm:text-sm text-emerald-700 dark:text-emerald-300 font-medium"
          >
            {{ nextEventHeadline }}
          </p>
        </template>
      </div>

      <div
        class="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 space-y-2"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {{ isAfterRamadan ? "Ramadan status" : "Aaj Pakistan mein kitna roza hai?" }}
        </h2>
        <p class="text-sm text-gray-700 dark:text-gray-300">
          <template v-if="isAfterRamadan">
            Ramadan ke tamam rozay mukammal ho chuke hain. {{ afterRamadanSubtext }}
          </template>
          <template v-else>
            Aaj Ramadan ka <span class="font-semibold">{{ displayRozaOrdinal }}</span>
            roza hai.
          </template>
        </p>
      </div>

      <!-- Section 2: Aaj Roza Kab Khulega? -->
      <div
        class="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 space-y-2"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {{ isAfterRamadan ? "Aakhri roza kab tha?" : "Aaj Roza Kab Khulega?" }}
        </h2>
        <template v-if="isAfterRamadan">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Aakhri roza <span class="font-semibold">{{ lastRozaDateLabel }}</span> ko tha.
            Us din iftar <span class="font-semibold">{{ lastRozaIftarDisplay }}</span> par hua.
          </p>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Ab roza countdown ki jagah Eid ki tayyari ka waqt hai.
          </p>
        </template>
        <template v-else>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Aaj ka iftar time:
            <span class="font-semibold">{{ iftarTimeDisplay }}</span>
          </p>
        </template>
        <p class="text-center text-sm">
          <NuxtLink
            to="/"
            class="text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            {{ isAfterRamadan ? "Full Ramadan timings dekhein" : "Full Iftar timings dekhein" }}
          </NuxtLink>
        </p>
      </div>

      <!-- Section 3: Roza Duration -->
      <div
        class="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 space-y-2"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {{ isAfterRamadan ? "Eid kab hai?" : "Roza Duration" }}
        </h2>
        <p class="text-sm text-gray-700 dark:text-gray-300">
          {{ isAfterRamadan ? eidDateAnswer : rozaDurationAnswer }}
        </p>
      </div>

      <div
        class="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 space-y-3"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {{ isAfterRamadan ? "Ramadan wrap-up" : "Ramadan Roza Count in Pakistan" }}
        </h2>
        <ul class="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li>1st Roza: {{ firstRozaDateLabel }}</li>
          <li>{{ isAfterRamadan ? "Last Roza" : "Today" }}: {{ isAfterRamadan ? lastRozaDateLabel : `${displayRozaOrdinal} Roza` }}</li>
          <li>{{ isAfterRamadan ? "Eid" : "Remaining Roza" }}: {{ isAfterRamadan ? eidDateLabel : remainingRozas }}</li>
        </ul>
      </div>

      <!-- Long-tail query line -->
      <p class="text-center text-sm text-gray-600 dark:text-gray-400">
        {{ isAfterRamadan ? "Ramadan 2026 ka roza safar mukammal ho chuka hai." : "Ramadan 2026 Pakistan mein total 30 roze hote hain." }}
      </p>

      <!-- Section 4: FAQ -->
      <div class="space-y-2">
        <h2 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          FAQ
        </h2>
        <details
          v-for="(faq, index) in faqs"
          :key="faq.question"
          :open="isDesktop && index === 0"
          class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3"
        >
          <summary
            class="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100"
          >
            {{ faq.question }}
          </summary>
          <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
            {{ faq.answer }}
          </p>
        </details>
      </div>

      <!-- Navigation -->
      <div class="pt-2 flex justify-center gap-2">
        <NuxtLink
          to="/calendar/"
          class="inline-flex items-center justify-center min-h-11 rounded-full border border-emerald-300 dark:border-emerald-700 px-4 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
        >
          Calendar
        </NuxtLink>
        <NuxtLink
          to="/"
          class="inline-flex items-center justify-center min-h-11 rounded-full border border-gray-300 dark:border-gray-600 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Home
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useRamadanTime } from "@/composables/useRamadanTime";
import { formatRomanHijriDate } from "@/composables/useRomanHijriDate";
import { computed, onMounted, onUnmounted, ref } from "vue";

const { now, normalizedDays, phase, today } = useRamadanTime();
const { generateFAQSchema, addSchema } = useSchemaMarkup();

const PAKISTAN_TIME_ZONE = "Asia/Karachi";
const TOTAL_RAMADAN_DAYS = 30;
const DEFAULT_HIJRI_YEAR = "1447";

const isDesktop = ref(false);
let desktopMediaQuery: MediaQueryList | null = null;
const syncDesktopState = () => {
  isDesktop.value = !!desktopMediaQuery?.matches;
};

const toPakistanDateTime = (dateIso: string, time24: string) =>
  new Date(`${dateIso}T${time24}:00+05:00`);

const formatClock12 = (time24: string) => {
  const [hh, mm] = time24.split(":");
  const h = Number(hh);
  if (!Number.isFinite(h) || !mm) return time24;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm} ${ampm}`;
};

const formatDurationHM = (ms: number) => {
  const safe = Math.max(0, ms);
  const totalMinutes = Math.floor(safe / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatRozaOrdinal = (value: number): string => {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return `${value}st`;
  if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
  return `${value}th`;
};

const firstRamadanDate = computed(() => {
  return (
    normalizedDays.value.find((day) => day.isRamadan)?.gregorianDate ||
    "2026-02-19"
  );
});

const lastRamadanDay = computed(() => {
  for (let i = normalizedDays.value.length - 1; i >= 0; i -= 1) {
    const day = normalizedDays.value[i];
    if (day?.isRamadan) return day;
  }
  return null;
});

const ramadanStartAt = computed(
  () => new Date(`${firstRamadanDate.value}T00:00:00+05:00`),
);

const remainingMs = computed(() =>
  Math.max(0, ramadanStartAt.value.getTime() - now.value.getTime()),
);

const isBeforeRamadan = computed(() => remainingMs.value > 0);

const todayInPakistanIso = computed(() => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PAKISTAN_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now.value);

  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  return `${year}-${month}-${day}`;
});

const nextPakistanMidnightAt = computed(() => {
  const todayStartAt = new Date(`${todayInPakistanIso.value}T00:00:00+05:00`);
  return new Date(todayStartAt.getTime() + 24 * 60 * 60 * 1000);
});

const todayRamadanDay = computed(
  () =>
    normalizedDays.value.find(
      (day) => day.gregorianDate === todayInPakistanIso.value && day.isRamadan,
    ) || null,
);

const isRozaToday = computed(() => !!todayRamadanDay.value?.isRamadan);

const todayRamadanIndex = computed(() =>
  normalizedDays.value.findIndex(
    (day) => day.gregorianDate === todayInPakistanIso.value && day.isRamadan,
  ),
);

const tomorrowRamadanDay = computed(() => {
  if (todayRamadanIndex.value < 0) return null;
  const next = normalizedDays.value[todayRamadanIndex.value + 1];
  return next?.isRamadan ? next : null;
});

const rozaDayNumber = computed(() => {
  const n = Number(todayRamadanDay.value?.rozaNumber ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
});

const currentRamadanYear = computed(
  () => firstRamadanDate.value.match(/^\d{4}/)?.[0] || "2026",
);
const displayRozaDayNumber = computed(() => rozaDayNumber.value || 1);
const displayRozaOrdinal = computed(() =>
  formatRozaOrdinal(displayRozaDayNumber.value),
);

const eidDateIso = computed(() => {
  const lastDate = lastRamadanDay.value?.gregorianDate;
  if (!lastDate) return null;

  const base = new Date(`${lastDate}T00:00:00+05:00`);
  const target = new Date(base.getTime() + 24 * 60 * 60 * 1000);

  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
});

const lastRozaIftarAt = computed(() => {
  if (!lastRamadanDay.value?.gregorianDate || !lastRamadanDay.value.iftarTime) {
    return null;
  }
  return toPakistanDateTime(
    lastRamadanDay.value.gregorianDate,
    lastRamadanDay.value.iftarTime,
  );
});

const isAfterLastRozaIftar = computed(() => {
  if (!lastRamadanDay.value?.gregorianDate || !lastRozaIftarAt.value) return false;
  return (
    todayInPakistanIso.value === lastRamadanDay.value.gregorianDate &&
    now.value.getTime() >= lastRozaIftarAt.value.getTime()
  );
});

const isAfterRamadan = computed(
  () => phase.value === "AFTER_RAMADAN" || isAfterLastRozaIftar.value,
);

const isEidTodayState = computed(
  () => !!eidDateIso.value && todayInPakistanIso.value === eidDateIso.value,
);

const isEidTomorrowState = computed(() => {
  if (!eidDateIso.value || !isAfterRamadan.value || isEidTodayState.value) {
    return false;
  }

  const todayStart = new Date(`${todayInPakistanIso.value}T00:00:00+05:00`);
  const eidStart = new Date(`${eidDateIso.value}T00:00:00+05:00`);
  return eidStart.getTime() - todayStart.getTime() === 24 * 60 * 60 * 1000;
});

const todayDateLabel = computed(() => {
  const d = new Date(now.value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: PAKISTAN_TIME_ZONE,
  }).format(d);
});

const firstRozaDateLabel = computed(() => {
  const firstRozaDate = new Date(`${firstRamadanDate.value}T00:00:00+05:00`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: PAKISTAN_TIME_ZONE,
  }).format(firstRozaDate);
});

const lastRozaDateLabel = computed(() => {
  if (!lastRamadanDay.value?.gregorianDate) return "Not available";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: PAKISTAN_TIME_ZONE,
  }).format(new Date(`${lastRamadanDay.value.gregorianDate}T00:00:00+05:00`));
});

const eidDateLabel = computed(() => {
  if (!eidDateIso.value) return "Moon sighting ke mutabiq";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: PAKISTAN_TIME_ZONE,
  }).format(new Date(`${eidDateIso.value}T00:00:00+05:00`));
});

const lastRozaIftarDisplay = computed(() => {
  if (!lastRamadanDay.value?.iftarTime) return "Not available";
  return formatClock12(lastRamadanDay.value.iftarTime);
});

const remainingRozas = computed(() => {
  if (todayRamadanDay.value) {
    return Math.max(0, TOTAL_RAMADAN_DAYS - rozaDayNumber.value);
  }

  return isBeforeRamadan.value ? TOTAL_RAMADAN_DAYS : 0;
});

const pageHeading = computed(() => {
  if (isAfterRamadan.value) {
    if (isEidTodayState.value) {
      return `Aaj Eid Hai - Eid ul-Fitr (${eidDateLabel.value})`;
    }

    return isEidTomorrowState.value
      ? `Rozay End Ho Chuky Hain - Kal Eid Hai (${eidDateLabel.value})`
      : `Ramadan ${currentRamadanYear.value} Complete - Eid Update Pakistan`;
  }

  if (todayRamadanDay.value) {
    return `Aaj Kitna Roza Hai? (${displayRozaOrdinal.value} Roza Today in Pakistan - Ramadan ${currentRamadanYear.value})`;
  }

  return `Aaj Kitna Roza Hai? Today Roza Number in Pakistan (Ramadan ${currentRamadanYear.value})`;
});

const snippetParagraph = computed(() => {
  if (isAfterRamadan.value) {
    if (isEidTodayState.value) {
      return `Aaj ${eidDateLabel.value} ko Eid ul-Fitr hai. Ramadan ${currentRamadanYear.value} ke rozay mukammal ho chuke hain.`;
    }

    return isEidTomorrowState.value
      ? `Ramadan ${currentRamadanYear.value} ke rozay mukammal ho chuke hain. Aaj chand raat hai aur kal ${eidDateLabel.value} ko Eid hai.`
      : `Ramadan ${currentRamadanYear.value} complete ho gaya hai. Yahan aakhri roza aur Eid date ka update dekhein.`;
  }

  return `Aaj Pakistan mein Ramadan ka ${displayRozaOrdinal.value} roza hai. Ramadan ${currentRamadanYear.value} mein Pakistan mein total ${TOTAL_RAMADAN_DAYS} rozay honge. Yahan aaj ka roza number, roza countdown aur Sehri Iftar timing dekhein.`;
});

const afterRamadanHeadline = computed(() =>
  isEidTodayState.value
    ? "Aaj Eid hai"
    : isEidTomorrowState.value
      ? "Kal Eid hai"
      : "Ramadan complete ho gaya",
);

const afterRamadanSubtext = computed(() =>
  isEidTodayState.value
    ? `Eid ul-Fitr ${eidDateLabel.value} ko hai. Eid Mubarak.`
    : isEidTomorrowState.value
    ? `InshaAllah ${eidDateLabel.value} ko Eid ul-Fitr hogi.`
    : `Eid ul-Fitr ${eidDateLabel.value} ko expected hai.`
);

const heroSupportCopy = computed(() => {
  if (isAfterRamadan.value) {
    if (isEidTodayState.value) {
      return "Ramadan ke rozay mukammal ho chuke hain aur aaj Eid ul-Fitr ka din hai. Namaz, dua aur ghar walon ke saath khushi manane ka waqt hai.";
    }

    return isEidTomorrowState.value
      ? "Ramadan ke rozay poore ho gaye hain. Aaj chand raat hai, ghar walon ke saath Eid ki tayyari, fitrana aur shukr ka waqt hai."
      : "Ramadan ka mubarak mahina mukammal ho gaya hai. Ab Eid ul-Fitr ki khushi aur shukr ka waqt hai.";
  }

  return `Aaj Pakistan mein Ramadan ka ${displayRozaOrdinal.value} roza hai. Yahan roza number, iftar countdown aur Sehri Iftar timing dekhein.`;
});

const afterRamadanHighlights = computed(() => [
  {
    label: "Aakhri Roza",
    value: lastRozaDateLabel.value,
  },
  {
    label: "Expected Eid",
    value: eidDateLabel.value,
  },
  {
    label: "Aaj Ki Baat",
    value: isEidTodayState.value
      ? "Eid Mubarak"
      : isEidTomorrowState.value
        ? "Chand raat"
        : "Eid update",
  },
]);

const seoDescription = computed(() => {
  if (isAfterRamadan.value) {
    if (isEidTodayState.value) {
      return `Aaj Eid ul-Fitr ${eidDateLabel.value} ko hai. Aakhri roza ${lastRozaDateLabel.value} ko tha.`;
    }

    return isEidTomorrowState.value
      ? `Rozay end ho chuky hain aur kal Eid hai. Aakhri roza ${lastRozaDateLabel.value} ko tha aur Eid ${eidDateLabel.value} ko expected hai.`
      : `Ramadan ${currentRamadanYear.value} complete ho gaya hai. Aakhri roza ki date aur Eid update Pakistan yahan dekhein.`;
  }

  if (todayRamadanDay.value) {
    return `Aaj Pakistan mein Ramadan ka ${displayRozaOrdinal.value} roza hai. Ramadan ${currentRamadanYear.value} mein Pakistan mein total ${TOTAL_RAMADAN_DAYS} rozay honge. Yahan aaj ka roza number, roza countdown aur Sehri Iftar timing dekhein.`;
  }

  return `Aaj kitna roza hai? Pakistan Ramadan ${currentRamadanYear.value} ka today roza number, live roza countdown, pehla roza ki date, aur remaining roza count yahan dekhein.`;
});

const nextEvent = computed(() => {
  if (!todayRamadanDay.value) {
    const firstRamadanDay =
      normalizedDays.value.find((day) => day.isRamadan) || null;
    if (!firstRamadanDay) return null;
    return {
      label: "Sehri",
      targetAt: toPakistanDateTime(
        firstRamadanDay.gregorianDate,
        firstRamadanDay.sehriEnds,
      ),
    };
  }

  const iftarAt = toPakistanDateTime(
    todayRamadanDay.value.gregorianDate,
    todayRamadanDay.value.iftarTime,
  );

  if (now.value.getTime() < iftarAt.getTime()) {
    return { label: "Iftar", targetAt: iftarAt };
  }

  if (tomorrowRamadanDay.value) {
    if (now.value.getTime() < nextPakistanMidnightAt.value.getTime()) {
      return null;
    }

    return {
      label: "Sehri",
      targetAt: toPakistanDateTime(
        tomorrowRamadanDay.value.gregorianDate,
        tomorrowRamadanDay.value.sehriEnds,
      ),
    };
  }

  return null;
});

const nextEventRemainingMs = computed(() => {
  if (!nextEvent.value) return 0;
  return Math.max(0, nextEvent.value.targetAt.getTime() - now.value.getTime());
});

const nextEventHeadline = computed(() => {
  if (isAfterRamadan.value) {
    return afterRamadanHeadline.value;
  }

  if (!nextEvent.value) {
    if (
      todayRamadanDay.value &&
      tomorrowRamadanDay.value &&
      now.value.getTime() < nextPakistanMidnightAt.value.getTime()
    ) {
      return "Sehri countdown starts after 12 AM";
    }

    return "Ramadan complete for today";
  }

  return `${nextEvent.value.label} in ${formatDurationHM(nextEventRemainingMs.value)}`;
});

const countdownItems = computed(() => {
  const totalSeconds = Math.floor(nextEventRemainingMs.value / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    { label: "Din", value: String(days).padStart(2, "0") },
    { label: "Ghantay", value: String(hours).padStart(2, "0") },
    { label: "Minute", value: String(minutes).padStart(2, "0") },
    { label: "Second", value: String(seconds).padStart(2, "0") },
  ];
});

// Iftar Time Display
const iftarTimeDisplay = computed(() => {
  if (!todayRamadanDay.value) return "Not available";
  return formatClock12(todayRamadanDay.value.iftarTime);
});

const sehriTimeDisplay = computed(() => {
  if (!todayRamadanDay.value) return "Not available";
  return formatClock12(todayRamadanDay.value.sehriEnds);
});

const tomorrowRozaNumber = computed(() => {
  const n = Number(tomorrowRamadanDay.value?.rozaNumber ?? 0);
  return Number.isFinite(n) && n > 0 ? n : null;
});

// Roza Duration
const rozaDurationAnswer = computed(() => {
  if (!todayRamadanDay.value)
    return "Aaj ka roza duration Ramadan day par show hota hai.";
  const sehriAt = toPakistanDateTime(
    todayRamadanDay.value.gregorianDate,
    todayRamadanDay.value.sehriEnds,
  );
  const iftarAt = toPakistanDateTime(
    todayRamadanDay.value.gregorianDate,
    todayRamadanDay.value.iftarTime,
  );
  const diff = iftarAt.getTime() - sehriAt.getTime();
  const totalMinutes = Math.max(0, Math.floor(diff / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `Aaj ka roza ${hours} ghante ${minutes} minute ka hai.`;
});

const eidDateAnswer = computed(() =>
  isEidTodayState.value
    ? `Aaj ${eidDateLabel.value} ko Eid ul-Fitr hai. Ramadan ke rozay kal mukammal hue thay.`
    : isEidTomorrowState.value
      ? `Kal ${eidDateLabel.value} ko Eid ul-Fitr expected hai. Aaj chand raat hai aur Ramadan ke rozay mukammal ho chuke hain.`
      : `Eid ul-Fitr ${eidDateLabel.value} ko expected hai. Exact tareekh moon sighting ke mutabiq confirm hoti hai.`
);

const faqs = computed(() => [
  {
    question: "Aaj Pakistan mein konsa roza hai?",
    answer: isAfterRamadan.value
      ? "Ramadan ke rozay mukammal ho chuke hain, is liye aaj roza number ke bajaye Eid update show ho raha hai."
      : `Aaj Pakistan mein Ramadan ka ${displayRozaOrdinal.value} roza hai. Is page par daily roza number Pakistan time ke mutabiq update hota hai.`,
  },
  {
    question: "Kal Pakistan mein konsa roza hoga?",
    answer: isAfterRamadan.value
      ? isEidTodayState.value
        ? "Aaj Eid hai, is liye kal ke roza number ka sawal apply nahi hota."
        : isEidTomorrowState.value
        ? `Kal ${eidDateLabel.value} ko Eid ul-Fitr expected hai, is liye kal roza nahi hoga.`
        : "Ramadan ke baad kal ka roza number apply nahi hota; yahan Eid update show hota hai."
      : tomorrowRozaNumber.value
        ? `Kal Pakistan mein Ramadan ka ${formatRozaOrdinal(tomorrowRozaNumber.value)} roza hoga.`
        : "Kal ka roza number Ramadan schedule ke aglay din ke mutabiq yahan show hota hai.",
  },
  {
    question: "Ramadan ka 23rd roza kab hota hai?",
    answer: isAfterRamadan.value
      ? "Ramadan ke specific rozon ki date calendar aur schedule section mein dekhi ja sakti hai."
      : `Ramadan ka ${displayRozaOrdinal.value} roza Pakistan mein ${todayDateLabel.value} ko hai, jo ${todayHijri.value} hota hai.`,
  },
  {
    question: "Pakistan mein Ramadan ka calendar kya hai?",
    answer: isAfterRamadan.value
      ? `Pakistan Ramadan ${currentRamadanYear.value} calendar mein pehla roza ${firstRozaDateLabel.value} aur aakhri roza ${lastRozaDateLabel.value} ko tha. Eid ${eidDateLabel.value} ko expected hai.`
      : `Pakistan Ramadan ${currentRamadanYear.value} calendar mein daily roza number, Sehri time, Iftar time, aur remaining rozay ka update milta hai. Ramadan ${currentRamadanYear.value} mein total ${TOTAL_RAMADAN_DAYS} rozay expected hain.`,
  },
]);

const todayHijri = computed(() => {
  if (today.value?.hijriDate) {
    const raw = today.value.hijriDate.trim();
    if (/\b\d{4}\b/.test(raw)) {
      return /AH$/i.test(raw) ? raw : `${raw} AH`;
    }
    return `${raw} ${DEFAULT_HIJRI_YEAR} AH`;
  }
  const formatted = formatRomanHijriDate(now.value, PAKISTAN_TIME_ZONE);

  if (isBeforeRamadan.value && /Ramadan/i.test(formatted)) {
    const yearMatch = formatted.match(/(\d{4})\s*AH/i);
    const hijriYear = yearMatch?.[1] ?? "";
    return `29 Shaban${hijriYear ? ` ${hijriYear}` : ""} AH`.trim();
  }

  return formatted;
});

onMounted(() => {
  desktopMediaQuery = window.matchMedia("(min-width: 640px)");
  syncDesktopState();
  desktopMediaQuery.addEventListener("change", syncDesktopState);
});

onUnmounted(() => {
  desktopMediaQuery?.removeEventListener("change", syncDesktopState);
});

useSeoMeta({
  title: () => pageHeading.value,
  description: () => seoDescription.value,
  ogTitle: () => pageHeading.value,
  ogDescription: () => seoDescription.value,
  ogType: "website",
  ogUrl: "https://rozay.today/roza-countdown",
  twitterCard: "summary_large_image",
  twitterTitle: () => pageHeading.value,
  twitterDescription: () => seoDescription.value,
});

useHead({
  link: [{ rel: "canonical", href: "https://rozay.today/roza-countdown" }],
});

addSchema(generateFAQSchema([...faqs.value]));
</script>

